/**
 * DisconnectIntegrationDialog Component
 *
 * Confirmation dialog before disconnecting a per-client integration.
 * Visually matches the rest of the app's confirm dialogs (Archive, Add Client).
 */

import { useState, useCallback } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@provenleap/ui";
import { Plugs } from "@phosphor-icons/react";
import { apiDelete } from "../../lib/apiClient";
import type { ToastType } from "../../hooks/useToast";

// ============================================================================
// Types
// ============================================================================

export interface DisconnectResult {
  deleted: boolean;
  lastConnection: boolean;
  oauthTokenId: string | null;
}

interface DisconnectIntegrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  integrationId: string;
  platformName: string;
  accountName: string | null;
  clientName: string;
  showToast: (type: ToastType, message: string) => void;
  onDisconnected: (result: DisconnectResult) => void;
}

// ============================================================================
// Component
// ============================================================================

export function DisconnectIntegrationDialog({
  open,
  onOpenChange,
  integrationId,
  platformName,
  accountName,
  clientName,
  showToast,
  onDisconnected,
}: DisconnectIntegrationDialogProps) {
  const [disconnecting, setDisconnecting] = useState(false);

  const handleDisconnect = useCallback(async () => {
    setDisconnecting(true);
    try {
      const res = await apiDelete(`/api/integrations/${integrationId}`);

      if (!res.ok) {
        const errorJson = await res.json().catch(() => null);
        const detail =
          (errorJson as { detail?: string })?.detail ?? "Failed to disconnect integration";
        showToast("error", detail);
        return;
      }

      const json = (await res.json()) as DisconnectResult;

      showToast(
        "success",
        `Disconnected ${accountName ?? platformName} from ${clientName}`,
      );

      onOpenChange(false);
      onDisconnected(json);
    } catch {
      showToast("error", "Failed to disconnect integration. Please try again.");
    } finally {
      setDisconnecting(false);
    }
  }, [integrationId, platformName, accountName, clientName, showToast, onOpenChange, onDisconnected]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-[calc(100vw-2rem)] sm:max-w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <div className="flex flex-col space-y-1.5 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    Disconnect {accountName ?? platformName}?
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    This will stop syncing {accountName ?? platformName} data for{" "}
                    {clientName}. Previously synced data is not affected.
                  </DialogPrimitive.Description>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6">
                    <Button
                      variant="ghost"
                      className="rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                      style={{ border: "0.5px solid hsl(var(--input))" }}
                      onClick={() => onOpenChange(false)}
                      disabled={disconnecting}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      className="rounded-xl cursor-pointer"
                      onClick={handleDisconnect}
                      disabled={disconnecting}
                    >
                      <Plugs size={16} className="mr-1.5" />
                      {disconnecting ? "Disconnecting..." : "Disconnect"}
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
